import { useId, type ReactNode } from 'react'

/** The Compass mark: a gradient disc with a needle that settles toward north when the page loads. Decorative; the name is next to it. */
export function Logo({ size = 28 }: { size?: number }) {
  const id = useId()
  return (
    <svg className="logo" width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={`${id}g`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#4338ca" /><stop offset=".55" stopColor="#7c3aed" /><stop offset="1" stopColor="#0ea5e9" />
        </linearGradient>
      </defs>
      <circle cx="24" cy="24" r="22" fill={`url(#${id}g)`} />
      <circle cx="24" cy="24" r="17.5" fill="none" stroke="#fff" strokeOpacity=".4" strokeWidth="1" strokeDasharray="2 3" />
      <g className="logo-needle">
        <path d="M24 8 29 24H19z" fill="#fff" />
        <path d="M24 40 29 24H19z" fill="#fff" fillOpacity=".45" />
      </g>
      <circle cx="24" cy="24" r="2.6" fill="#0f1633" stroke="#fff" strokeWidth="1.2" />
    </svg>
  )
}

export function Wordmark({ suffix }: { suffix?: string }) {
  return <span className="wordmark"><Logo /> <span>Compass Assess{suffix && <small>{suffix}</small>}</span></span>
}

/** The big animated illustration beside sign-in and registration on desktop screens. Purely decorative and hidden on small screens. */
export function HeroArt() {
  const id = useId()
  const ring = (r: number, n: number, cls: string, dot: number) => (
    <g className={`orbit ${cls}`}>
      <circle cx="260" cy="260" r={r} fill="none" stroke={`url(#${id}r)`} strokeWidth="1.2" strokeDasharray="3 7" />
      {Array.from({ length: n }, (_, i) => {
        const a = (i / n) * Math.PI * 2
        return <circle key={i} className="node" style={{ animationDelay: `${i * 0.4}s` }} cx={260 + r * Math.cos(a)} cy={260 + r * Math.sin(a)} r={dot} fill={i % 3 === 0 ? '#22d3ee' : i % 3 === 1 ? '#a78bfa' : '#f0abfc'} />
      })}
    </g>
  )
  const ticks = Array.from({ length: 36 }, (_, i) => {
    const a = (i / 36) * Math.PI * 2
    const long = i % 9 === 0
    return <line key={i} x1={260 + 64 * Math.cos(a)} y1={260 + 64 * Math.sin(a)} x2={260 + (long ? 52 : 57) * Math.cos(a)} y2={260 + (long ? 52 : 57) * Math.sin(a)} stroke="#fff" strokeOpacity={long ? 0.9 : 0.4} strokeWidth={long ? 2 : 1} />
  })
  return (
    <svg className="hero-art" viewBox="0 0 520 520" aria-hidden="true" focusable="false">
      <defs>
        <radialGradient id={`${id}a`}><stop offset="0" stopColor="#7c3aed" stopOpacity=".55" /><stop offset=".6" stopColor="#4338ca" stopOpacity=".18" /><stop offset="1" stopColor="#4338ca" stopOpacity="0" /></radialGradient>
        <linearGradient id={`${id}r`} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#22d3ee" /><stop offset="1" stopColor="#c084fc" /></linearGradient>
        <radialGradient id={`${id}f`} cx=".4" cy=".35"><stop offset="0" stopColor="#ffffff" stopOpacity=".22" /><stop offset="1" stopColor="#ffffff" stopOpacity=".04" /></radialGradient>
      </defs>
      <circle className="pulse" cx="260" cy="260" r="250" fill={`url(#${id}a)`} />
      {ring(222, 12, 'o1', 4.5)}
      {ring(160, 8, 'o2', 5)}
      {ring(104, 5, 'o3', 4)}
      <circle cx="260" cy="260" r="72" fill={`url(#${id}f)`} stroke="#fff" strokeOpacity=".35" />
      {ticks}
      <g className="needle">
        <path d="M260 196 271 260H249z" fill="#22d3ee" />
        <path d="M260 324 271 260H249z" fill="#fff" fillOpacity=".5" />
      </g>
      <circle cx="260" cy="260" r="5" fill="#0b1020" stroke="#fff" strokeWidth="2" />
      <text x="260" y="186" textAnchor="middle" fill="#fff" fillOpacity=".8" fontSize="12" fontWeight="700">N</text>
    </svg>
  )
}

export interface HeroCopy { eyebrow: string; title: string; text: string; points: string[]; chips: [string, string, string] }

export function Hero({ copy }: { copy: HeroCopy }) {
  return (
    <aside className="hero" aria-hidden="true">
      <div className="hero-copy">
        <span className="eyebrow">{copy.eyebrow}</span>
        <h2>{copy.title}</h2>
        <p>{copy.text}</p>
        <ul>{copy.points.map((p) => <li key={p}><svg viewBox="0 0 20 20" width="18" height="18"><circle cx="10" cy="10" r="9" fill="none" stroke="currentColor" strokeOpacity=".5" /><path d="m6 10.5 2.6 2.6L14 7.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>{p}</li>)}</ul>
      </div>
      <div className="hero-stage">
        <HeroArt />
        <span className="chip-float c1">{copy.chips[0]}</span>
        <span className="chip-float c2">{copy.chips[1]}</span>
        <span className="chip-float c3">{copy.chips[2]}</span>
      </div>
    </aside>
  )
}

/** Two columns on desktop (illustration and form), a single plain column on phones. */
export function SplitLayout({ hero, children }: { hero: HeroCopy; children: ReactNode }) {
  return <div className="split"><Hero copy={hero} /><div className="split-main">{children}</div></div>
}
